import React, { useState } from 'react';
import { X, Copy, Check, MapPin, House, MapPinSimpleArea, LinkSimple } from '@phosphor-icons/react';

/**
 * Floating right-side panel shown when the user clicks a cadastre parcel.
 * Mirrors the visual style of ShapePropertiesPanel for consistency.
 * Props:
 *   info: { address, lat, lng, areaM2, state, details: { parcelStatus, parcelType, planNumber, lotNumber, locality } }
 *   onClose: () => void
 *   onAddMarker: (lat, lng, label) => void
 */
export default function PropertyInfoPanel({ info, onClose, onAddMarker }) {
  const [linkCopied, setLinkCopied] = useState(false);
  const [markerAdded, setMarkerAdded] = useState(false);

  if (!info) return null;

  const d = info.details || {};
  const fmtArea = (m2) => (!m2 || m2 <= 0) ? '—' : `${m2.toFixed(2)} m²`;
  const orDash = (v) => (v === null || v === undefined || v === '') ? '-' : v;

  const handleCopyLink = async () => {
    const link = `https://www.google.com/maps?q=${info.lat.toFixed(6)},${info.lng.toFixed(6)}`;
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch (e) {
      // Clipboard permission denied (or insecure context). The user can still
      // copy the lat/lng row above manually — surface a debug log for support.
      console.debug('clipboard writeText (copy link) failed:', e);
    }
  };

  const handleAddMarker = () => {
    if (!onAddMarker) return;
    const label = info.address ? info.address.split(',')[0] : `${info.lat.toFixed(5)}, ${info.lng.toFixed(5)}`;
    onAddMarker(info.lat, info.lng, label);
    setMarkerAdded(true);
    setTimeout(() => setMarkerAdded(false), 1500);
  };

  return (
    <div
      className="absolute right-3 top-14 z-30 w-[300px] bg-card border border-border rounded-xl shadow-2xl animate-fadeIn"
      data-testid="property-info-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
            <House size={14} className="text-primary" weight="duotone" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold leading-tight truncate">Property Info</p>
            {info.state && (
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{info.state} Cadastre</p>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="ml-2 shrink-0 w-6 h-6 flex items-center justify-center rounded-md hover:bg-secondary/80 text-muted-foreground"
          data-testid="property-info-close"
        >
          <X size={14} weight="bold" />
        </button>
      </div>

      <div className="border-t border-border mx-3" />

      <div className="px-3 py-3 space-y-3">
        {/* Address */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
            <MapPin size={11} weight="duotone" /> Address
          </p>
          {info.address ? (
            <CopyRow text={info.address} testId="property-address" />
          ) : info.addressLoading ? (
            <p className="text-xs text-muted-foreground italic">Looking up address…</p>
          ) : (
            <p className="text-xs text-muted-foreground italic">No address available for this point.</p>
          )}
        </div>

        {/* Coordinates */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
            <MapPin size={11} weight="duotone" /> Coordinates
          </p>
          <CopyRow
            text={`${info.lat.toFixed(6)}, ${info.lng.toFixed(6)}`}
            testId="property-latlng"
            mono
          />
        </div>

        {/* Parcel Details */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Parcel Details</p>
          <div className="space-y-1.5 text-[12px] bg-secondary/40 rounded-md px-3 py-2.5">
            <DetailRow label="Area" value={fmtArea(info.areaM2)} testId="property-area" />
            <DetailRow label="Parcel Status" value={orDash(d.parcelStatus)} testId="property-parcel-status" />
            <DetailRow label="Parcel Type" value={orDash(d.parcelType)} testId="property-parcel-type" />
            <DetailRow label="Plan Number" value={orDash(d.planNumber)} testId="property-plan-number" />
            <DetailRow label="Lot Number" value={orDash(d.lotNumber)} testId="property-lot-number" />
            <DetailRow label="Locality" value={orDash(d.locality)} testId="property-locality" />
            <DetailRow label="State" value={orDash(info.state)} testId="property-state" />
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={handleAddMarker}
            disabled={!onAddMarker}
            data-testid="property-add-marker-btn"
            className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-md bg-primary/10 hover:bg-primary/15 text-primary text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {markerAdded ? (
              <><Check size={13} weight="bold" /> Added</>
            ) : (
              <><MapPinSimpleArea size={13} weight="duotone" /> Add Marker</>
            )}
          </button>
          <button
            type="button"
            onClick={handleCopyLink}
            data-testid="property-copy-link-btn"
            className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-md bg-secondary/70 hover:bg-secondary text-foreground/80 text-xs font-semibold transition-colors"
          >
            {linkCopied ? (
              <><Check size={13} weight="bold" className="text-emerald-500" /> Copied</>
            ) : (
              <><LinkSimple size={13} weight="duotone" /> Copy Link</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3" data-testid={testId}>
      <span className="text-muted-foreground shrink-0">{label}:</span>
      <span className="font-medium text-foreground/90 text-right break-words">{value}</span>
    </div>
  );
}

function CopyRow({ text, testId, mono = false }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      // Clipboard permission denied — the visible text is still selectable.
      console.debug('clipboard writeText (CopyRow) failed:', e);
    }
  };
  return (
    <div className="flex items-start justify-between gap-2 bg-secondary/40 rounded-md px-2.5 py-2">
      <p
        className={`text-xs leading-relaxed break-words flex-1 ${mono ? 'font-mono' : ''}`}
        data-testid={testId}
      >
        {text}
      </p>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
        title="Copy"
        data-testid={`${testId}-copy`}
      >
        {copied ? <Check size={12} weight="bold" className="text-emerald-500" /> : <Copy size={12} />}
      </button>
    </div>
  );
}
